<template>
  <div class="min-h-screen bg-gray-50/50 pb-12">
    <div
      class="max-w-none xl:max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 space-y-6"
    >
      <!-- Page Title & Top Actions -->
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm"
      >
        <div class="flex items-center space-x-3.5">
          <div
            class="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-600 to-indigo-700 flex items-center justify-center text-white shadow-lg shadow-indigo-500/20"
          >
            <Wallet class="w-6 h-6" />
          </div>
          <div>
            <h1 class="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
              Fee Management System
            </h1>
            <p class="text-xs sm:text-sm text-slate-500 font-medium">
              Manage admission fees, monthly recurring obligations, receipts, and reports
            </p>
          </div>
        </div>

        <!-- Session Selector & Refresh -->
        <div class="flex items-center space-x-3">
          <div class="flex items-center space-x-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200">
            <span class="text-xs font-semibold text-slate-500">Session:</span>
            <select
              v-model="selectedSession"
              @change="handleSessionChange"
              class="bg-transparent text-xs font-bold text-slate-800 outline-none cursor-pointer"
            >
              <option value="">All Sessions</option>
              <option v-for="s in availableSessions" :key="s" :value="s">
                {{ s }}
              </option>
            </select>
          </div>

          <button
            @click="refreshData"
            :disabled="isDashboardLoading || isStudentsLoading"
            class="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 transition border border-slate-200"
            title="Refresh Fee Data"
          >
            <RefreshCw
              :class="[
                'w-4 h-4',
                isDashboardLoading || isStudentsLoading ? 'animate-spin' : '',
              ]"
            />
          </button>
        </div>
      </div>

      <!-- Dashboard Summary Cards (6 Cards) -->
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <!-- 1. Total Admission Expected -->
        <div class="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm relative overflow-hidden">
          <div class="flex items-center justify-between mb-2">
            <span class="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Admission Expected
            </span>
            <div class="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <GraduationCap class="w-3.5 h-3.5" />
            </div>
          </div>
          <div class="text-base sm:text-lg font-black text-slate-900 font-mono">
            ₹{{ stats.totalAdmissionExpected.toLocaleString("en-IN") }}
          </div>
          <span class="text-[10px] text-slate-400 font-medium">One-time enrollment</span>
        </div>

        <!-- 2. Total Monthly Expected -->
        <div class="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm relative overflow-hidden">
          <div class="flex items-center justify-between mb-2">
            <span class="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Monthly Expected
            </span>
            <div class="w-7 h-7 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
              <Calendar class="w-3.5 h-3.5" />
            </div>
          </div>
          <div class="text-base sm:text-lg font-black text-slate-900 font-mono">
            ₹{{ stats.totalMonthlyExpected.toLocaleString("en-IN") }}
          </div>
          <span class="text-[10px] text-slate-400 font-medium">Period obligations</span>
        </div>

        <!-- 3. Total Admission Collected -->
        <div class="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm relative overflow-hidden">
          <div class="flex items-center justify-between mb-2">
            <span class="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Admission Collected
            </span>
            <div class="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 class="w-3.5 h-3.5" />
            </div>
          </div>
          <div class="text-base sm:text-lg font-black text-emerald-600 font-mono">
            ₹{{ stats.totalAdmissionCollected.toLocaleString("en-IN") }}
          </div>
          <span class="text-[10px] text-emerald-600 font-medium">Collected to date</span>
        </div>

        <!-- 4. Total Monthly Collected -->
        <div class="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm relative overflow-hidden">
          <div class="flex items-center justify-between mb-2">
            <span class="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Monthly Collected
            </span>
            <div class="w-7 h-7 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
              <CheckCircle class="w-3.5 h-3.5" />
            </div>
          </div>
          <div class="text-base sm:text-lg font-black text-teal-600 font-mono">
            ₹{{ stats.totalMonthlyCollected.toLocaleString("en-IN") }}
          </div>
          <span class="text-[10px] text-teal-600 font-medium">Monthly fees paid</span>
        </div>

        <!-- 5. Total Outstanding Fees -->
        <div class="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm relative overflow-hidden">
          <div class="flex items-center justify-between mb-2">
            <span class="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Total Outstanding
            </span>
            <div class="w-7 h-7 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
              <AlertCircle class="w-3.5 h-3.5" />
            </div>
          </div>
          <div class="text-base sm:text-lg font-black text-red-600 font-mono">
            ₹{{ stats.totalOutstanding.toLocaleString("en-IN") }}
          </div>
          <span class="text-[10px] text-red-500 font-medium">Admission + Monthly</span>
        </div>

        <!-- 6. Students with Pending Fees -->
        <div class="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm relative overflow-hidden">
          <div class="flex items-center justify-between mb-2">
            <span class="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Students Pending
            </span>
            <div class="w-7 h-7 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <Users class="w-3.5 h-3.5" />
            </div>
          </div>
          <div class="text-base sm:text-lg font-black text-amber-600 font-mono">
            {{ stats.studentsWithPendingFees }}
          </div>
          <span class="text-[10px] text-amber-600 font-medium">Due / Overdue accounts</span>
        </div>
      </div>

      <!-- Navigation Tabs: Students & Fees vs Reports vs Transactions -->
      <div class="flex items-center space-x-2 border-b border-slate-200">
        <button
          @click="activeTab = 'students'"
          :class="[
            'px-5 py-3 text-sm font-bold border-b-2 transition-all flex items-center gap-2',
            activeTab === 'students'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-slate-500 hover:text-slate-800',
          ]"
        >
          <Users class="w-4 h-4" />
          Students & Fees Ledger
        </button>

        <button
          @click="activeTab = 'reports'"
          :class="[
            'px-5 py-3 text-sm font-bold border-b-2 transition-all flex items-center gap-2',
            activeTab === 'reports'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-slate-500 hover:text-slate-800',
          ]"
        >
          <FileText class="w-4 h-4" />
          Fee Reports
        </button>

        <button
          @click="activeTab = 'transactions'"
          :class="[
            'px-5 py-3 text-sm font-bold border-b-2 transition-all flex items-center gap-2',
            activeTab === 'transactions'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-slate-500 hover:text-slate-800',
          ]"
        >
          <Receipt class="w-4 h-4" />
          All Transactions
        </button>
      </div>

      <!-- TAB 1: STUDENTS & FEES LEDGER -->
      <div v-if="activeTab === 'students'" class="space-y-4">
        <!-- Controls Bar: Class Dropdown, Search Input, Status Filter -->
        <div
          class="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3"
        >
          <div class="flex flex-wrap items-center gap-3 flex-1">
            <!-- Class Dropdown (Requirement 3) -->
            <div class="w-full sm:w-56">
              <label class="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1">
                Class
              </label>
              <select
                v-model="selectedClassId"
                @change="handleClassChange"
                class="w-full px-3 py-2 text-sm font-medium rounded-xl border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="">All Classes</option>
                <option
                  v-for="cls in classesList"
                  :key="cls._id"
                  :value="cls._id"
                >
                  {{ cls.name }}{{ cls.section ? ` - ${cls.section}` : "" }}
                </option>
              </select>
            </div>

            <!-- Student Search (Requirement 4) -->
            <div class="w-full sm:w-64">
              <label class="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1">
                Search Student
              </label>
              <div class="relative">
                <Search class="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                <input
                  v-model="searchQuery"
                  @input="handleSearchInput"
                  type="text"
                  placeholder="Name, Roll No, Phone, ID..."
                  class="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
            </div>

            <!-- Status Filter -->
            <div class="w-full sm:w-44">
              <label class="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1">
                Fee Status
              </label>
              <select
                v-model="statusFilter"
                @change="fetchStudents"
                class="w-full px-3 py-2 text-sm font-medium rounded-xl border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="all">All Statuses</option>
                <option value="unconfigured">Not Configured</option>
                <option value="paid">Fully Paid</option>
                <option value="partial">Partially Paid</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
          </div>

          <!-- Actions: Reset Filter & Configure Class Fee -->
          <div class="flex items-end gap-2.5">
            <button
              v-if="selectedClassId || searchQuery || statusFilter !== 'all'"
              @click="resetFilters"
              class="px-3.5 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl transition"
            >
              Reset Filters
            </button>
            <button
              @click="openClassFeeModal(selectedClassId)"
              class="px-4 py-2 text-xs font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 rounded-xl shadow-md shadow-indigo-500/20 transition flex items-center gap-1.5"
            >
              <Layers class="w-4 h-4" />
              <span>Configure Class Fee</span>
            </button>
          </div>
        </div>

        <!-- Class Fee Rate Banner (when a class is selected) -->
        <div
          v-if="selectedClassId && selectedClassInfo"
          class="border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs"
          :class="
            selectedClassFee?.isConfigured
              ? 'bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 border-indigo-200/80'
              : 'bg-amber-50/70 border-amber-200'
          "
        >
          <div class="flex items-center space-x-3">
            <div
              class="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm"
              :class="
                selectedClassFee?.isConfigured
                  ? 'bg-indigo-600 text-white'
                  : 'bg-amber-500 text-white'
              "
            >
              <Layers class="w-4 h-4" />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h4 class="text-xs font-bold text-slate-800 uppercase tracking-wide">
                  {{ selectedClassInfo.name }}{{ selectedClassInfo.section ? ` - ${selectedClassInfo.section}` : "" }} Standard Fee Structure
                </h4>
                <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                  {{ selectedClassStudentCount }} students
                </span>
                <span
                  v-if="!selectedClassFee?.isConfigured"
                  class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                >
                  Not Set
                </span>
              </div>
              <p v-if="selectedClassFee?.isConfigured" class="text-xs text-slate-600 mt-0.5">
                One-Time Admission: <span class="font-bold text-slate-900 font-mono">₹{{ (selectedClassFee?.admissionFee || 0).toLocaleString("en-IN") }}</span>
                &nbsp;•&nbsp;
                Monthly Fee: <span class="font-bold text-slate-900 font-mono">₹{{ (selectedClassFee?.monthlyFee || 0).toLocaleString("en-IN") }}/mo</span>
                <span v-if="selectedClassFee?.dueDay" class="text-slate-500"> (Due on {{ selectedClassFee.dueDay }}th)</span>
              </p>
              <p v-else class="text-xs text-amber-800 mt-0.5">
                Standard fees not configured yet for this class. Set admission and monthly fees to apply to all students.
              </p>
            </div>
          </div>
          <button
            @click="openClassFeeModal(selectedClassId)"
            class="px-3.5 py-1.5 text-xs font-bold rounded-xl shadow-xs transition flex items-center gap-1.5 self-start sm:self-center"
            :class="
              selectedClassFee?.isConfigured
                ? 'text-indigo-700 hover:text-indigo-900 bg-white hover:bg-indigo-50 border border-indigo-200'
                : 'text-white bg-amber-600 hover:bg-amber-700'
            "
          >
            <Sliders class="w-3.5 h-3.5" />
            <span>{{ selectedClassFee?.isConfigured ? "Edit Class Fee" : "Set Class Fee" }}</span>
          </button>
        </div>

        <!-- Student Fee Table -->
        <div class="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <!-- Loading State -->
          <div v-if="isStudentsLoading" class="p-12 text-center flex flex-col items-center justify-center">
            <div class="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            <span class="text-xs font-medium text-slate-500 mt-3">Loading student fee records...</span>
          </div>

          <!-- Table Content -->
          <div v-else class="overflow-x-auto">
            <table class="w-full text-left text-xs">
              <thead class="bg-slate-50 text-slate-600 uppercase font-bold text-[11px] border-b border-slate-200">
                <tr>
                  <th class="py-3.5 px-4">Student</th>
                  <th class="py-3.5 px-4">Class</th>
                  <th class="py-3.5 px-4 text-center">Admission Fee</th>
                  <th class="py-3.5 px-4 text-center">Monthly Fee</th>
                  <th class="py-3.5 px-4 text-right">Total Paid</th>
                  <th class="py-3.5 px-4 text-right">Outstanding</th>
                  <th class="py-3.5 px-4 text-center">Status</th>
                  <th class="py-3.5 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 font-medium">
                <tr
                  v-for="s in studentsList"
                  :key="s._id"
                  class="hover:bg-slate-50/70 transition"
                >
                  <!-- Student Details -->
                  <td class="py-3 px-4">
                    <div class="flex items-center space-x-3">
                      <div class="w-9 h-9 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 overflow-hidden flex-shrink-0">
                        <img
                          v-if="s.photo"
                          :src="s.photo"
                          alt="Photo"
                          class="w-full h-full object-cover"
                        />
                        <User v-else class="w-4 h-4 text-slate-400" />
                      </div>
                      <div>
                        <div class="font-bold text-slate-900 text-sm">
                          {{ s.name }}
                        </div>
                        <div class="text-[11px] text-slate-400">
                          Roll: {{ s.rollNumber || "N/A" }}
                          <span v-if="s.attendanceID"> • {{ s.attendanceID }}</span>
                        </div>
                      </div>
                    </div>
                  </td>

                  <!-- Class -->
                  <td class="py-3 px-4 text-slate-700 font-semibold">
                    {{ s.class?.name || "N/A" }}
                    <span v-if="s.class?.section"> ({{ s.class.section }})</span>
                  </td>

                  <!-- Admission Fee -->
                  <td class="py-3 px-4 text-center">
                    <template v-if="s.isConfigured">
                      <div class="font-mono font-bold text-slate-800">
                        ₹{{ s.admissionFee?.amount?.toLocaleString("en-IN") || 0 }}
                      </div>
                      <span
                        class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase inline-block mt-0.5"
                        :class="
                          s.admissionFee?.status === 'paid'
                            ? 'bg-emerald-100 text-emerald-800'
                            : s.admissionFee?.status === 'partial'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-slate-100 text-slate-600'
                        "
                      >
                        {{ s.admissionFee?.status || "unpaid" }}
                      </span>
                    </template>
                    <span v-else class="text-xs font-semibold text-slate-400">Not Set</span>
                  </td>

                  <!-- Monthly Fee -->
                  <td class="py-3 px-4 text-center">
                    <template v-if="s.isConfigured">
                      <div class="font-mono font-bold text-slate-800">
                        ₹{{ s.monthlyFee?.amount?.toLocaleString("en-IN") || 0 }}/mo
                      </div>
                      <span
                        v-if="s.monthlyFee?.overdueCount > 0"
                        class="px-2 py-0.5 rounded-full text-[10px] font-black uppercase inline-block bg-red-100 text-red-800 mt-0.5"
                      >
                        {{ s.monthlyFee.overdueCount }} Overdue
                      </span>
                      <span
                        v-else-if="s.monthlyFee?.totalDue > 0 && s.monthlyFee?.balance === 0"
                        class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase inline-block bg-emerald-100 text-emerald-800 mt-0.5"
                      >
                        Up to Date
                      </span>
                      <span
                        v-else
                        class="px-2 py-0.5 rounded-full text-[10px] font-semibold text-slate-400 inline-block mt-0.5"
                      >
                        ₹{{ (s.monthlyFee?.balance || 0).toLocaleString("en-IN") }} due
                      </span>
                    </template>
                    <span v-else class="text-xs font-semibold text-slate-400">Not Set</span>
                  </td>

                  <!-- Paid Amount -->
                  <td class="py-3 px-4 text-right font-mono font-bold text-emerald-600 text-sm">
                    ₹{{ s.totalPaid?.toLocaleString("en-IN") || 0 }}
                  </td>

                  <!-- Outstanding Balance -->
                  <td class="py-3 px-4 text-right font-mono font-bold text-sm" :class="s.totalOutstanding > 0 ? 'text-red-600' : 'text-slate-400'">
                    ₹{{ s.totalOutstanding?.toLocaleString("en-IN") || 0 }}
                  </td>

                  <!-- Overall Status -->
                  <td class="py-3 px-4 text-center">
                    <span
                      class="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase inline-block"
                      :class="
                        s.overallStatus === 'paid'
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                          : s.overallStatus === 'partial'
                          ? 'bg-amber-100 text-amber-800 border border-amber-200'
                          : s.overallStatus === 'overdue'
                          ? 'bg-red-100 text-red-800 border border-red-200 font-black'
                          : s.overallStatus === 'pending'
                          ? 'bg-blue-100 text-blue-800 border border-blue-200'
                          : 'bg-slate-100 text-slate-500 border border-slate-200'
                      "
                    >
                      {{ s.overallStatus === 'unconfigured' ? 'Not Configured' : s.overallStatus }}
                    </span>
                  </td>

                  <!-- Actions -->
                  <td class="py-3 px-4 text-right">
                    <div class="flex items-center justify-end space-x-1.5">
                      <button
                        v-if="!s.isConfigured"
                        @click="openConfigureModal(s)"
                        class="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-bold transition border border-indigo-200 shadow-xs"
                      >
                        Set Fee
                      </button>
                      <button
                        @click="openStudentDetails(s._id)"
                        class="px-3 py-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition"
                      >
                        {{ s.isConfigured ? "Manage Fees" : "Details" }}
                      </button>
                      <button
                        v-if="s.isConfigured && s.totalOutstanding > 0"
                        @click="quickRecordPayment(s)"
                        class="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition shadow-sm"
                      >
                        Pay
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="studentsList.length === 0">
                  <td colspan="8" class="py-12 text-center text-slate-400">
                    <div class="flex flex-col items-center justify-center space-y-2">
                      <Users class="w-8 h-8 text-slate-300" />
                      <p class="font-medium text-slate-500">No students found matching the selected filters.</p>
                      <button
                        v-if="selectedClassId || searchQuery || statusFilter !== 'all'"
                        @click="resetFilters"
                        class="text-xs text-indigo-600 hover:underline font-bold"
                      >
                        Clear active filters
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="p-4 border-t border-slate-100">
            <Pagination
              :currentPage="pagination.page"
              :totalPages="pagination.pages"
              :totalResults="pagination.total"
              :limit="pagination.limit"
              :isLoading="isStudentsLoading"
              @go-to-page="goToPage"
            />
          </div>
        </div>
      </div>

      <!-- TAB 2: FEE REPORTS -->
      <div v-if="activeTab === 'reports'" class="space-y-4">
        <!-- Reports Controls Bar -->
        <div
          class="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm space-y-4"
        >
          <div class="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100">
            <div>
              <h3 class="text-sm font-bold text-slate-800">Fee Analytics & Reports</h3>
              <p class="text-xs text-slate-500">Filter, summarize and print authoritative fee collection reports</p>
            </div>
            <button
              @click="printReport"
              :disabled="isReportLoading || reportRecords.length === 0"
              class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow transition flex items-center gap-1.5 disabled:opacity-50"
            >
              <Printer class="w-3.5 h-3.5" />
              Print Report
            </button>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <!-- 1. Report Type Selector (10 reports) -->
            <div>
              <label class="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1">
                Report Type
              </label>
              <select
                v-model="reportFilter.type"
                @change="fetchReports"
                class="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="admission_collection">1. Admission Fee Collection</option>
                <option value="monthly_collection">2. Monthly Fee Collection</option>
                <option value="daily_collection">3. Daily Collections</option>
                <option value="monthly_collection_summary">4. Annual Collections Summary</option>
                <option value="outstanding_admission">5. Outstanding Admission Fees</option>
                <option value="outstanding_monthly">6. Outstanding Monthly Fees</option>
                <option value="student_outstanding">7. Student-wise Outstanding Balances</option>
                <option value="partially_paid">8. Students with Partially Paid Fees</option>
                <option value="overdue">9. Students with Overdue Fees</option>
                <option value="payment_history">10. Complete Payment History</option>
              </select>
            </div>

            <!-- 2. Class Filter -->
            <div>
              <label class="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1">
                Filter by Class
              </label>
              <select
                v-model="reportFilter.classId"
                @change="fetchReports"
                class="w-full px-3 py-2 text-xs font-medium rounded-xl border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="">All Classes</option>
                <option v-for="cls in classesList" :key="cls._id" :value="cls._id">
                  {{ cls.name }}{{ cls.section ? ` - ${cls.section}` : "" }}
                </option>
              </select>
            </div>

            <!-- 3. Start Date -->
            <div>
              <label class="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1">
                From Date
              </label>
              <input
                v-model="reportFilter.startDate"
                @change="fetchReports"
                type="date"
                class="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>

            <!-- 4. End Date -->
            <div>
              <label class="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1">
                To Date
              </label>
              <input
                v-model="reportFilter.endDate"
                @change="fetchReports"
                type="date"
                class="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
          </div>
        </div>

        <!-- Report Summary Stats -->
        <div
          v-if="reportSummary && Object.keys(reportSummary).length > 0"
          class="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-5 rounded-2xl shadow-md flex items-center justify-between"
        >
          <div>
            <h4 class="text-sm uppercase tracking-wider font-bold text-indigo-100">
              {{ reportSummary.reportTitle || "Report Summary" }}
            </h4>
            <div class="text-2xl font-black font-mono mt-1">
              {{
                reportSummary.totalCollected !== undefined
                  ? `₹${reportSummary.totalCollected.toLocaleString("en-IN")}`
                  : reportSummary.totalOutstanding !== undefined
                  ? `₹${reportSummary.totalOutstanding.toLocaleString("en-IN")}`
                  : `${reportSummary.totalCount} Records`
              }}
            </div>
          </div>
          <div class="text-right">
            <span class="text-xs text-indigo-100 block">Total Records</span>
            <span class="text-lg font-bold font-mono">{{ reportSummary.totalCount || reportRecords.length }}</span>
          </div>
        </div>

        <!-- Printable Report Container -->
        <div id="printable-report-section" class="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div v-if="isReportLoading" class="p-12 text-center flex flex-col items-center justify-center">
            <div class="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            <span class="text-xs font-medium text-slate-500 mt-3">Compiling report data...</span>
          </div>

          <div v-else class="overflow-x-auto">
            <!-- Dynamic Report Table based on type -->
            <table class="w-full text-left text-xs">
              <thead class="bg-slate-50 text-slate-600 uppercase font-bold text-[11px] border-b border-slate-200">
                <tr>
                  <th class="py-3 px-4">#</th>
                  <th v-if="reportFilter.type === 'monthly_collection_summary'" class="py-3 px-4">Month</th>
                  <th v-else class="py-3 px-4">Student</th>
                  <th v-if="reportFilter.type !== 'monthly_collection_summary'" class="py-3 px-4">Class</th>
                  <th v-if="['admission_collection', 'monthly_collection', 'daily_collection', 'payment_history'].includes(reportFilter.type)" class="py-3 px-4">Receipt</th>
                  <th v-if="['admission_collection', 'monthly_collection', 'daily_collection', 'payment_history'].includes(reportFilter.type)" class="py-3 px-4">Date</th>
                  <th v-if="['monthly_collection', 'outstanding_monthly', 'overdue'].includes(reportFilter.type)" class="py-3 px-4">Month/Year</th>
                  <th v-if="['admission_collection', 'monthly_collection', 'daily_collection', 'payment_history'].includes(reportFilter.type)" class="py-3 px-4">Method</th>
                  <th class="py-3 px-4 text-right">Amount / Due</th>
                  <th v-if="['outstanding_admission', 'outstanding_monthly', 'student_outstanding', 'partially_paid', 'overdue'].includes(reportFilter.type)" class="py-3 px-4 text-right">Balance</th>
                  <th class="py-3 px-4 text-center">Status</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 font-medium">
                <tr v-for="(rec, idx) in reportRecords" :key="idx" class="hover:bg-slate-50/70">
                  <td class="py-3 px-4 font-mono text-slate-400">{{ idx + 1 }}</td>

                  <!-- Monthly summary special row -->
                  <template v-if="reportFilter.type === 'monthly_collection_summary'">
                    <td class="py-3 px-4 font-bold text-slate-800">{{ rec.monthName }} {{ rec.year }}</td>
                    <td class="py-3 px-4 text-right font-mono font-bold text-emerald-700">₹{{ rec.totalCollected.toLocaleString("en-IN") }}</td>
                    <td class="py-3 px-4 text-center">{{ rec.transactionCount }} transactions</td>
                  </template>

                  <!-- Standard Rows -->
                  <template v-else>
                    <td class="py-3 px-4 font-bold text-slate-900">
                      {{ rec.studentId?.name || rec.student?.name || "N/A" }}
                    </td>
                    <td class="py-3 px-4 text-slate-600">
                      {{ formatStudentClass(rec.studentId || rec.student) }}
                    </td>
                    <td v-if="['admission_collection', 'monthly_collection', 'daily_collection', 'payment_history'].includes(reportFilter.type)" class="py-3 px-4 font-mono font-bold text-indigo-700">
                      {{ rec.receiptNumber }}
                    </td>
                    <td v-if="['admission_collection', 'monthly_collection', 'daily_collection', 'payment_history'].includes(reportFilter.type)" class="py-3 px-4 text-slate-600">
                      {{ formatDate(rec.paymentDate) }}
                    </td>
                    <td v-if="['monthly_collection', 'outstanding_monthly', 'overdue'].includes(reportFilter.type)" class="py-3 px-4 text-slate-700 font-semibold">
                      {{ rec.monthName }} {{ rec.year }}
                    </td>
                    <td v-if="['admission_collection', 'monthly_collection', 'daily_collection', 'payment_history'].includes(reportFilter.type)" class="py-3 px-4 text-slate-700">
                      {{ rec.paymentMethod }}
                    </td>
                    <td class="py-3 px-4 text-right font-mono font-bold text-emerald-600">
                      ₹{{ (rec.amount || rec.feeDue || rec.admissionAmount || rec.totalOutstanding || 0).toLocaleString("en-IN") }}
                    </td>
                    <td v-if="['outstanding_admission', 'outstanding_monthly', 'student_outstanding', 'partially_paid', 'overdue'].includes(reportFilter.type)" class="py-3 px-4 text-right font-mono font-bold text-red-600">
                      ₹{{ (rec.balance || rec.totalOutstanding || 0).toLocaleString("en-IN") }}
                    </td>
                    <td class="py-3 px-4 text-center">
                      <span
                        class="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase inline-block"
                        :class="
                          ['paid', 'completed'].includes(rec.status)
                            ? 'bg-emerald-100 text-emerald-800'
                            : rec.status === 'partial'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-red-100 text-red-800 font-black'
                        "
                      >
                        {{ rec.status || "due" }}
                      </span>
                    </td>
                  </template>
                </tr>

                <tr v-if="reportRecords.length === 0">
                  <td colspan="10" class="py-12 text-center text-slate-400">
                    No data records found for this report and filter criteria.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- TAB 3: ALL TRANSACTIONS & RECEIPTS -->
      <div v-if="activeTab === 'transactions'" class="space-y-4">
        <div class="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div class="p-4 border-b border-slate-100 flex items-center justify-between">
            <h4 class="text-sm font-bold text-slate-800">Master Fee Transaction Audit Log</h4>
            <span class="text-xs text-slate-400">{{ allTransactions.length }} transactions</span>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full text-left text-xs">
              <thead class="bg-slate-50 text-slate-600 uppercase font-bold text-[11px] border-b border-slate-200">
                <tr>
                  <th class="py-3 px-4">Receipt No</th>
                  <th class="py-3 px-4">Date</th>
                  <th class="py-3 px-4">Student</th>
                  <th class="py-3 px-4">Category</th>
                  <th class="py-3 px-4">Period</th>
                  <th class="py-3 px-4">Payment Method</th>
                  <th class="py-3 px-4 text-right">Amount Received</th>
                  <th class="py-3 px-4">Collected By</th>
                  <th class="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 font-medium">
                <tr v-for="tx in allTransactions" :key="tx._id" class="hover:bg-slate-50/70">
                  <td class="py-3 px-4 font-mono font-bold text-indigo-700">
                    {{ tx.receiptNumber }}
                  </td>
                  <td class="py-3 px-4 text-slate-600">{{ formatDate(tx.paymentDate) }}</td>
                  <td class="py-3 px-4 font-bold text-slate-900">
                    {{ tx.studentId?.name || "N/A" }}
                    <span class="text-slate-400 font-normal block text-[10px]">
                      {{ formatStudentClass(tx.studentId) }}
                    </span>
                  </td>
                  <td class="py-3 px-4 capitalize font-semibold text-slate-700">
                    {{ tx.feeCategory === "admission" ? "Admission Fee" : "Monthly Fee" }}
                  </td>
                  <td class="py-3 px-4 text-slate-600">
                    {{ tx.feeCategory === "monthly" && tx.monthName ? `${tx.monthName} ${tx.year}` : "One-Time" }}
                  </td>
                  <td class="py-3 px-4 text-slate-700">
                    {{ tx.paymentMethod }}
                    <span v-if="tx.transactionReference" class="text-[10px] text-slate-400 block font-mono">
                      Ref: {{ tx.transactionReference }}
                    </span>
                  </td>
                  <td class="py-3 px-4 text-right font-mono font-bold text-emerald-700 text-sm">
                    ₹{{ tx.amount.toLocaleString("en-IN") }}
                  </td>
                  <td class="py-3 px-4 text-slate-500 text-[11px]">
                    {{ tx.recordedByName || "Administrator" }}
                  </td>
                  <td class="py-3 px-4 text-right">
                    <button
                      @click="openReceiptModal(tx.receiptNumber)"
                      class="px-2.5 py-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition"
                    >
                      Receipt
                    </button>
                  </td>
                </tr>

                <tr v-if="allTransactions.length === 0">
                  <td colspan="9" class="py-12 text-center text-slate-400">
                    No transactions recorded yet.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <!-- 1. Student Fee Modal -->
    <StudentFeeModal
      :show="showStudentModal"
      :student="activeStudentDetails.student"
      :feeStructure="activeStudentDetails.feeStructure"
      :monthlySummary="activeStudentDetails.monthlySummary"
      :monthlyRecords="activeStudentDetails.monthlyRecords"
      :transactions="activeStudentDetails.transactions"
      :isLoading="isStudentDetailsLoading"
      @close="showStudentModal = false"
      @configure="openConfigureModal"
      @record-payment="openPaymentModal"
      @view-receipt="openReceiptModal"
    />

    <!-- 2. Configure Fee Modal -->
    <ConfigureFeeModal
      :show="showConfigureModal"
      :student="configureTargetStudent"
      :feeStructure="configureTargetFeeStructure"
      :isSubmitting="isConfiguring"
      @close="showConfigureModal = false"
      @save="handleSaveConfiguration"
    />

    <!-- 3. Record Payment Modal -->
    <RecordPaymentModal
      :show="showPaymentModal"
      :student="paymentTargetStudent"
      :feeStructure="paymentTargetFeeStructure"
      :monthlyRecords="paymentTargetMonthlyRecords"
      :preselectedCategory="paymentPreselectedCategory"
      :preselectedMonthlyRecordId="paymentPreselectedRecordId"
      :isSubmitting="isRecordingPayment"
      @close="showPaymentModal = false"
      @submit="handleSavePayment"
    />

    <!-- 4. Fee Receipt Modal -->
    <FeeReceiptModal
      :show="showReceiptModal"
      :receipt="activeReceipt"
      @close="showReceiptModal = false"
    />

    <!-- 5. Configure Class Fee Modal -->
    <ConfigureClassFeeModal
      :show="showClassFeeModal"
      :initialClassId="selectedClassId"
      :classes="classesList"
      :isSubmitting="isConfiguringClassFee"
      @close="showClassFeeModal = false"
      @save="handleSaveClassFeeConfiguration"
    />
  </div>
</template>

<script setup>
  import { ref, reactive, computed, onMounted } from "vue";
  import { useStore } from "vuex";
  import {
    Wallet,
    GraduationCap,
    Calendar,
    Users,
    CheckCircle,
    CheckCircle2,
    AlertCircle,
    RefreshCw,
    Search,
    FileText,
    Receipt,
    Printer,
    User,
    Layers,
    Sliders,
  } from "lucide-vue-next";

  import Pagination from "@/components/Students/Pagination.vue";
  import StudentFeeModal from "@/components/FeeManagement/StudentFeeModal.vue";
  import ConfigureFeeModal from "@/components/FeeManagement/ConfigureFeeModal.vue";
  import ConfigureClassFeeModal from "@/components/FeeManagement/ConfigureClassFeeModal.vue";
  import RecordPaymentModal from "@/components/FeeManagement/RecordPaymentModal.vue";
  import FeeReceiptModal from "@/components/FeeManagement/FeeReceiptModal.vue";
  import { useGlobalToast } from "@/utils/useGlobalToast";

  import {
    getDashboardStats,
    getStudentsFeeList,
    getStudentFeeDetails,
    configureStudentFee,
    recordPayment,
    getReceipt,
    getFeeReports,
    getClassFeeStructure,
    configureClassFee,
  } from "@/services/feeService";

  const store = useStore();
  const { showToast } = useGlobalToast();

  // Active Tab: 'students' | 'reports' | 'transactions'
  const activeTab = ref("students");

  // Dashboard Stats State
  const isDashboardLoading = ref(false);
  const selectedSession = ref("");
  const availableSessions = ref([]);
  const stats = reactive({
    totalAdmissionExpected: 0,
    totalMonthlyExpected: 0,
    totalAdmissionCollected: 0,
    totalMonthlyCollected: 0,
    totalOutstanding: 0,
    studentsWithPendingFees: 0,
  });

  // Students Table State
  const isStudentsLoading = ref(false);
  const studentsList = ref([]);
  const selectedClassId = ref("");
  const searchQuery = ref("");
  const statusFilter = ref("all");
  const pagination = reactive({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });

  // Classes list from Vuex store
  const classesList = computed(() => {
    const list = store.getters.allClasses || [];
    return list.filter((c) => (c?.name || "").toLowerCase() !== "passout");
  });

  // Modals state
  const showStudentModal = ref(false);
  const isStudentDetailsLoading = ref(false);
  const activeStudentDetails = reactive({
    student: null,
    feeStructure: null,
    monthlySummary: null,
    monthlyRecords: [],
    transactions: [],
  });

  const showConfigureModal = ref(false);
  const isConfiguring = ref(false);
  const configureTargetStudent = ref(null);
  const configureTargetFeeStructure = ref(null);

  const showPaymentModal = ref(false);
  const isRecordingPayment = ref(false);
  const paymentTargetStudent = ref(null);
  const paymentTargetFeeStructure = ref(null);
  const paymentTargetMonthlyRecords = ref([]);
  const paymentPreselectedCategory = ref("monthly");
  const paymentPreselectedRecordId = ref("");

  const showReceiptModal = ref(false);
  const activeReceipt = ref(null);

  // Class Fee Configuration state
  const showClassFeeModal = ref(false);
  const isConfiguringClassFee = ref(false);
  const selectedClassFee = ref(null);
  const selectedClassInfo = ref(null);
  const selectedClassStudentCount = ref(0);

  const fetchSelectedClassFee = async () => {
    if (!selectedClassId.value) {
      selectedClassFee.value = null;
      selectedClassInfo.value = null;
      selectedClassStudentCount.value = 0;
      return;
    }
    try {
      const res = await getClassFeeStructure(selectedClassId.value);
      if (res.data?.success) {
        selectedClassFee.value = res.data.data.feeStructure;
        selectedClassInfo.value = res.data.data.classInfo;
        selectedClassStudentCount.value = res.data.data.studentCount || 0;
      }
    } catch (err) {
      console.error("fetchSelectedClassFee error:", err);
    }
  };

  const openClassFeeModal = (classId = "") => {
    if (classId) {
      selectedClassId.value = classId;
    }
    showClassFeeModal.value = true;
  };

  const handleSaveClassFeeConfiguration = async (payload) => {
    try {
      isConfiguringClassFee.value = true;
      const res = await configureClassFee(payload.classId, payload);
      if (res.data?.success) {
        showToast(
          "success",
          res.data.message || "Class fee structure saved and applied to students!"
        );
        showClassFeeModal.value = false;
        await fetchSelectedClassFee();
        await fetchStudents();
        await fetchDashboardStats();
      }
    } catch (err) {
      console.error("handleSaveClassFeeConfiguration error:", err);
      const msg = err.response?.data?.message || "Failed to configure class fee";
      showToast("error", msg);
    } finally {
      isConfiguringClassFee.value = false;
    }
  };

  // Reports state
  const isReportLoading = ref(false);
  const reportFilter = reactive({
    type: "payment_history",
    classId: "",
    startDate: "",
    endDate: "",
  });
  const reportSummary = ref({});
  const reportRecords = ref([]);

  // All Transactions list for Tab 3
  const allTransactions = ref([]);

  // Fetch Dashboard Stats
  const fetchDashboardStats = async () => {
    try {
      isDashboardLoading.value = true;
      const res = await getDashboardStats({
        session: selectedSession.value || undefined,
        classId: selectedClassId.value || undefined,
      });

      if (res.data?.success) {
        const d = res.data.data;
        stats.totalAdmissionExpected = d.totalAdmissionExpected || 0;
        stats.totalMonthlyExpected = d.totalMonthlyExpected || 0;
        stats.totalAdmissionCollected = d.totalAdmissionCollected || 0;
        stats.totalMonthlyCollected = d.totalMonthlyCollected || 0;
        stats.totalOutstanding = d.totalOutstanding || 0;
        stats.studentsWithPendingFees = d.studentsWithPendingFees || 0;
        if (d.availableSessions?.length) {
          availableSessions.value = d.availableSessions;
        }
      }
    } catch (err) {
      console.error("fetchDashboardStats error:", err);
      showToast("error", "Failed to load fee dashboard statistics");
    } finally {
      isDashboardLoading.value = false;
    }
  };

  // Fetch Students Fee Overview Table
  let searchTimeout = null;
  const handleSearchInput = () => {
    if (searchTimeout) clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      pagination.page = 1;
      fetchStudents();
    }, 400);
  };

  const handleClassChange = () => {
    pagination.page = 1;
    fetchStudents();
    fetchDashboardStats();
    fetchSelectedClassFee();
  };

  const handleSessionChange = () => {
    pagination.page = 1;
    fetchStudents();
    fetchDashboardStats();
  };

  const resetFilters = () => {
    selectedClassId.value = "";
    selectedClassFee.value = null;
    selectedClassInfo.value = null;
    selectedClassStudentCount.value = 0;
    searchQuery.value = "";
    statusFilter.value = "all";
    pagination.page = 1;
    fetchStudents();
    fetchDashboardStats();
  };

  const goToPage = (page) => {
    pagination.page = page;
    fetchStudents();
  };

  const fetchStudents = async () => {
    try {
      isStudentsLoading.value = true;
      const res = await getStudentsFeeList({
        classId: selectedClassId.value || undefined,
        search: searchQuery.value.trim() || undefined,
        status: statusFilter.value,
        session: selectedSession.value || undefined,
        page: pagination.page,
        limit: pagination.limit,
      });

      if (res.data?.success) {
        studentsList.value = res.data.students || [];
        if (res.data.pagination) {
          pagination.page = res.data.pagination.page;
          pagination.limit = res.data.pagination.limit;
          pagination.total = res.data.pagination.total;
          pagination.pages = res.data.pagination.pages;
        }
      }
    } catch (err) {
      console.error("fetchStudents error:", err);
      showToast("error", "Failed to load student fee records");
    } finally {
      isStudentsLoading.value = false;
    }
  };

  // Open Student Details Modal
  const openStudentDetails = async (studentId) => {
    try {
      isStudentDetailsLoading.value = true;
      showStudentModal.value = true;

      const res = await getStudentFeeDetails(studentId);
      if (res.data?.success) {
        const d = res.data.data;
        activeStudentDetails.student = d.student;
        activeStudentDetails.feeStructure = d.feeStructure;
        activeStudentDetails.monthlySummary = d.monthlySummary;
        activeStudentDetails.monthlyRecords = d.monthlyRecords || [];
        activeStudentDetails.transactions = d.transactions || [];
      }
    } catch (err) {
      console.error("openStudentDetails error:", err);
      showToast("error", "Failed to load student fee ledger");
      showStudentModal.value = false;
    } finally {
      isStudentDetailsLoading.value = false;
    }
  };

  // Quick Open Configure Modal
  const openConfigureModal = (student) => {
    configureTargetStudent.value = student || activeStudentDetails.student;
    configureTargetFeeStructure.value = activeStudentDetails.feeStructure;
    showConfigureModal.value = true;
  };

  const handleSaveConfiguration = async (configData) => {
    if (!configureTargetStudent.value?._id) return;
    try {
      isConfiguring.value = true;
      const res = await configureStudentFee(
        configureTargetStudent.value._id,
        configData
      );

      if (res.data?.success) {
        showToast("success", "Fee configuration saved successfully!");
        showConfigureModal.value = false;

        // Refresh modals and main table
        await fetchStudents();
        await fetchDashboardStats();
        if (showStudentModal.value) {
          await openStudentDetails(configureTargetStudent.value._id);
        }
      }
    } catch (err) {
      console.error("handleSaveConfiguration error:", err);
      const msg = err.response?.data?.message || "Failed to save fee configuration";
      showToast("error", msg);
    } finally {
      isConfiguring.value = false;
    }
  };

  // Open Record Payment Modal
  const openPaymentModal = ({
    student,
    feeStructure,
    monthlyRecords,
    category = "monthly",
    monthlyFeeRecordId = "",
  }) => {
    paymentTargetStudent.value = student;
    paymentTargetFeeStructure.value = feeStructure;
    paymentTargetMonthlyRecords.value = monthlyRecords || [];
    paymentPreselectedCategory.value = category;
    paymentPreselectedRecordId.value = monthlyFeeRecordId;
    showPaymentModal.value = true;
  };

  const quickRecordPayment = async (student) => {
    try {
      const res = await getStudentFeeDetails(student._id);
      if (res.data?.success) {
        const d = res.data.data;
        openPaymentModal({
          student: d.student,
          feeStructure: d.feeStructure,
          monthlyRecords: d.monthlyRecords,
        });
      }
    } catch (err) {
      console.error("quickRecordPayment error:", err);
      showToast("error", "Failed to initialize payment form");
    }
  };

  const handleSavePayment = async (paymentData) => {
    if (!paymentTargetStudent.value?._id) return;
    try {
      isRecordingPayment.value = true;
      const res = await recordPayment(
        paymentTargetStudent.value._id,
        paymentData
      );

      if (res.data?.success) {
        showToast(
          "success",
          res.data.message || "Payment recorded successfully!"
        );
        showPaymentModal.value = false;

        // Open receipt modal right away
        if (res.data.receiptNumber) {
          await openReceiptModal(res.data.receiptNumber);
        }

        // Refresh student records & dashboard
        await fetchStudents();
        await fetchDashboardStats();

        // Refresh student details modal if open
        if (showStudentModal.value) {
          await openStudentDetails(paymentTargetStudent.value._id);
        }
      }
    } catch (err) {
      console.error("handleSavePayment error:", err);
      const msg = err.response?.data?.message || "Failed to record payment";
      showToast("error", msg);
    } finally {
      isRecordingPayment.value = false;
    }
  };

  // Open Receipt Modal
  const openReceiptModal = async (receiptNumber) => {
    try {
      const res = await getReceipt(receiptNumber);
      if (res.data?.success) {
        activeReceipt.value = res.data.receipt;
        showReceiptModal.value = true;
      }
    } catch (err) {
      console.error("openReceiptModal error:", err);
      showToast("error", "Failed to retrieve receipt details");
    }
  };

  // Fetch Reports
  const fetchReports = async () => {
    try {
      isReportLoading.value = true;
      const res = await getFeeReports({
        type: reportFilter.type,
        classId: reportFilter.classId || undefined,
        startDate: reportFilter.startDate || undefined,
        endDate: reportFilter.endDate || undefined,
      });

      if (res.data?.success) {
        reportSummary.value = res.data.summary || {};
        reportRecords.value = res.data.records || [];
      }
    } catch (err) {
      console.error("fetchReports error:", err);
      showToast("error", "Failed to load fee reports");
    } finally {
      isReportLoading.value = false;
    }
  };

  const printReport = () => {
    window.print();
  };

  // Fetch All Transactions (Tab 3)
  const fetchAllTransactions = async () => {
    try {
      const res = await getFeeReports({ type: "payment_history" });
      if (res.data?.success) {
        allTransactions.value = res.data.records || [];
      }
    } catch (err) {
      console.error("fetchAllTransactions error:", err);
    }
  };

  // Refresh All
  const refreshData = async () => {
    await fetchDashboardStats();
    await fetchStudents();
    if (activeTab.value === "reports") await fetchReports();
    if (activeTab.value === "transactions") await fetchAllTransactions();
    showToast("success", "Fee data refreshed successfully");
  };

  // Format helpers
  const formatDate = (d) => {
    if (!d) return "N/A";
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatStudentClass = (student) => {
    if (!student || !student.class) return "N/A";
    if (typeof student.class === "string") return student.class;
    let out = student.class.name || "N/A";
    if (student.class.section) out += ` - ${student.class.section}`;
    return out;
  };

  // Lifecycle
  onMounted(async () => {
    await store.dispatch("fetchAllClasses");
    await fetchDashboardStats();
    await fetchStudents();
    await fetchReports();
    await fetchAllTransactions();
  });
</script>
